import { useEffect, useState } from "react";

import {
	ActionStatusHelper,
	ChartSerie,
	DateFormatHelper,
	OptionHelper,
	DomainResultModel,
	FinancialResultModel,
	ActionResponse,
	RecordMap,
	Entity,
} from "@/Shared";


import {
	useActivity,
	useContact,
	useMandate,
	useMatching,
	useNeed,
	usePermission,
	usePlanning,
	useProperty,
	useTransaction,
	useUser
} from "@/OneLiners";

import { ResultFilterCrtieria } from "@/Shared/UI/Components/ResultFilter/ResultFilter";
import { useAccessRight } from "@/Domains";


export const keyToLabelFinancialMap: Record<keyof Omit<FinancialResultModel, 'date'>, string> = {
	ca: "Chiffre d'affaires",
	com: "Commissions",
};

export const keyToLabelDomainMap: Record<keyof Omit<DomainResultModel, 'date'>, string> = {
	mandats: 'Mandats',
	transactions: 'Transactions',
	contacts: 'Contacts',
	properties: 'Biens',
	needs: 'Demandes',
	plannings: 'Rendez-vous',
	matchings: 'Rap. Auto',
	activities: 'Activités',
};

const chartSeries = {
	financials: [
		{ key: 'ca', color: '#00c49f', type: 'Line' },
		{ key: 'com', color: '#0088fe', type: 'Line' },
	] as ChartSerie[],
	domains: [
		{ key: 'mandats', color: '#8884d8', type: 'Line' },
		{ key: 'transactions', color: '#00c49f', type: 'Line' },
		{ key: 'contacts', color: '#ff7300', type: 'Line' },
		{ key: 'properties', color: '#0088fe', type: 'Line' },
		{ key: 'needs', color: '#ff0000', type: 'Line' },
		{ key: 'plannings', color: '#3b4146ff', type: 'Line' },
		{ key: 'activities', color: '#9d6a04ff', type: 'Line' },
		{ key: 'matchings', color: '#a832a6', type: 'Line' },
	] as ChartSerie[]
};

export function useResultEnricher() {

	const [resultFilter, setResultFilter] = useState<ResultFilterCrtieria>({
		isCustomDate: false,
		year: new Date().getFullYear(),
		fromDate: '',
		toDate: '',
		userId: undefined,
	});

	const { dataMap: activityMap, stateResponse: activityResponse } = useActivity();
	const { dataMap: matchingMap, stateResponse: matchingResponse } = useMatching();
	const { dataMap: planningMap, stateResponse: planningResponse} = usePlanning();
	const { dataMap: propertyMap, stateResponse: propertyResponse } = useProperty();
	const { dataMap: needMap, stateResponse: needResponse} = useNeed();
	const { dataMap: transactionMap, stateResponse: transactionResponse } = useTransaction();
	const { dataMap: mandateMap, stateResponse: mandateResponse} = useMandate();
	const { dataMap: contactMap, stateResponse: contactResponse} = useContact();
	const { dataMap: userMap, stateResponse: userResponse } = useUser();
	const { dataMap: permissionMap, stateResponse: permissionResponse } = usePermission();

	const [data, setData] = useState<{ financials: FinancialResultModel[], domains: DomainResultModel[] }>({ financials: [], domains: [] });
	
	const accessRight = useAccessRight(permissionMap);

	const [response, setResponse] = useState<ActionResponse>({ status: 'loading' });

	const combinedStatus = ActionStatusHelper.combine(
		activityResponse.status,
		matchingResponse.status,
		planningResponse.status,
		propertyResponse.status,
		needResponse.status,
		transactionResponse.status,
		contactResponse.status,
		mandateResponse.status,
		userResponse.status,
		permissionResponse.status
	);

	const error = activityResponse.error
		?? matchingResponse.error
		?? planningResponse.error
		?? propertyResponse.error
		?? needResponse.error
		?? transactionResponse.error
		?? contactResponse.error
		?? mandateResponse.error
		?? userResponse.error
		?? permissionResponse.error;
		

	useEffect(() => {

		if (ActionStatusHelper.isSucceeded(combinedStatus)) {
			updateData();
		}
		if (!(ActionStatusHelper.isLoading(combinedStatus) || ActionStatusHelper.isIdle(combinedStatus))) {
			setResponse({
				status: combinedStatus,
				error
			});
		}
	}, [combinedStatus, error, resultFilter]);

	const handleFilterChange = (partial: Partial<ResultFilterCrtieria>) => {
		setResultFilter(prev => ({ ...prev, ...partial }));
	}

	const filterRawData = (map: RecordMap<any>) => {
		return map.filter((item: any) => {
			const createdAt = new Date(item.createdAt);
			const createdBy = item.createdBy;

			if (resultFilter.userId && createdBy !== resultFilter.userId) {
				return false;
			}

			if (resultFilter.isCustomDate) {
				if (resultFilter.fromDate) {
					const from = new Date(resultFilter.fromDate);
					if (createdAt < from) {
						return false;
					}
				}
				if (resultFilter.toDate) {
					const to = new Date(resultFilter.toDate);
					if (createdAt > to) {
						return false;
					}
				}
			} else if (resultFilter.year) {
				if (createdAt.getFullYear() !== resultFilter.year) {
					return false;
				}
			}

			return true;
		});
	}

	const updateData = () => {

		const rawDomainDataByType = {
			mandats: mandateMap,
			transactions: transactionMap,
			contacts: contactMap,
			properties: propertyMap,
			needs: needMap,
			plannings: planningMap,
			activities: activityMap,
			matchings: matchingMap,
		};

		const aggregatedDomainMap: Record<string, DomainResultModel> = {};
		let filteredTransactionMap = undefined;

		Object.entries(rawDomainDataByType).forEach(([type, map]) => {

			const filteredMap = filterRawData(map);

			filteredMap.forEach((item: any) => {
				const date = DateFormatHelper.toInput(item.createdAt);

				if (!aggregatedDomainMap[date]) {
					aggregatedDomainMap[date] = {
						date,
						mandats: 0,
						transactions: 0,
						contacts: 0,
						properties: 0,
						needs: 0,
						plannings: 0,
						matchings: 0,
						activities: 0,
					};
				}
				// ✅ Incrémente le compteur du type courant
				aggregatedDomainMap[date][type as keyof DomainResultModel]++;

				if (type === 'transactions') {
					filteredTransactionMap = filteredMap;
				}

			});
		});

		const aggregatedFinancialMap: Record<string, FinancialResultModel> = {};

		if(filteredTransactionMap) {
			(filteredTransactionMap as RecordMap<any>).forEach((item: any) => {
				const date = DateFormatHelper.toInput(item.createdAt);

				if (!aggregatedFinancialMap[date]) {
					aggregatedFinancialMap[date] = {
						date,
						ca: 0,
						com: 0,
					}
				}
				aggregatedFinancialMap[date]['ca'] += Number(item.operationPrice || 0);
				aggregatedFinancialMap[date]['com'] += Number(item.agencyCom || 0);
			});
		}

		const domains = Object.values(aggregatedDomainMap).sort((a, b) => {
			return new Date(a.date).getTime() - new Date(b.date).getTime();
		});

		const financials = Object.values(aggregatedFinancialMap).sort((a, b) => {
			return new Date(a.date).getTime() - new Date(b.date).getTime();
		});

		setData({
			domains,
			financials
		});
	}

	return {
		response,
		data,
		filter: resultFilter,
		keyToLabelMap: {
			domains: keyToLabelDomainMap,
			financials: keyToLabelFinancialMap
		},
		chartSeries,
		handleFilterChange,
		userMap,
		userOptions: OptionHelper.userOptions(userMap.toArray()),
		accessRight
	}
}